import { router } from "@inertiajs/react";
import { useState } from "react";
import Header from "./Component/Header";

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("id-ID", {
        year: "numeric",
        month: "long",
        day: "2-digit",
    }).format(date);
};

const getStatusMeta = (status) => {
    if (status === 1) {
        return {
            label: "Berhasil",
            className:
                "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
        };
    }

    if (status === 2) {
        return {
            label: "Kedaluwarsa",
            className:
                "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
        };
    }

    return {
        label: "Menunggu",
        className:
            "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    };
};

export default function SalesOrderDetail({
    data = null,
    message = null,
    filters = {},
}) {
    const statusMeta = getStatusMeta(Number(data?.status ?? 0));
    const [soCode, setSoCode] = useState(filters?.soCode ?? "");
    const [momoId, setMomoId] = useState(filters?.momoId ?? "");

    const handleSearch = (event) => {
        event.preventDefault();

        router.get(
            "/sales-order",
            {
                soCode: soCode.trim(),
                momoId: momoId.trim(),
            },
            {
                preserveState: true,
                preserveScroll: true,
            }
        );
    };

    return (
        <>
            <Header />
            <main id="content">
                <div className="w-full max-w-4xl mx-auto p-4">
                    <div className="bg-card border border-card-line rounded-lg shadow-md p-5 space-y-4">
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <h1 className="text-lg font-semibold text-foreground">Lacak Pesanan</h1>
                                <p className="mt-1 text-sm text-muted-foreground-1">
                                    Cari detail pesanan berdasarkan kode SO dan ID Momo.
                                </p>
                            </div>
                            <a
                                href="/profile"
                                className="inline-flex items-center rounded-lg border border-line-2 px-3 py-2 text-sm text-foreground hover:bg-layer"
                            >
                                Kembali ke Profil
                            </a>
                        </div>

                        <form
                            onSubmit={handleSearch}
                            className="rounded-xl border border-line-2 bg-layer p-4"
                        >
                            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
                                <div>
                                    <label
                                        htmlFor="tracking-so-code"
                                        className="mb-1.5 block text-sm font-medium text-foreground"
                                    >
                                        SO Code
                                    </label>
                                    <input
                                        id="tracking-so-code"
                                        type="text"
                                        value={soCode}
                                        onChange={(event) => setSoCode(event.target.value)}
                                        placeholder="Masukkan kode SO"
                                        className="w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-fuchsia-400/60"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="tracking-momo-id"
                                        className="mb-1.5 block text-sm font-medium text-foreground"
                                    >
                                        Momo ID
                                    </label>
                                    <input
                                        id="tracking-momo-id"
                                        type="text"
                                        value={momoId}
                                        onChange={(event) => setMomoId(event.target.value)}
                                        placeholder="Masukkan ID Momo"
                                        className="w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-fuchsia-400/60"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="inline-flex items-center justify-center rounded-lg border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
                                >
                                    Cari
                                </button>
                            </div>
                        </form>

                        {!data ? (
                            <div className="rounded-lg border border-line-2 bg-layer p-4 text-sm text-muted-foreground-1">
                                {message ?? "Data sales order belum tersedia."}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">SO Code</p>
                                    <p className="font-medium text-foreground">{data.so_code ?? "-"}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Momo ID</p>
                                    <p className="font-medium text-foreground">{data.rec_creator ?? "-"}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Produk</p>
                                    <p className="font-medium text-foreground">{data.product_name ?? "-"}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Varian</p>
                                    <p className="font-medium text-foreground">{data.variant_name ?? "-"}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Tanggal</p>
                                    <p className="font-medium text-foreground">{formatDate(data.created_at)}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Total</p>
                                    <p className="font-medium text-foreground">{formatCurrency(data.total_price)}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Status Pembayaran</p>
                                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusMeta.className}`}>
                                        {statusMeta.label}
                                    </span>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Kode Invoice</p>
                                    <p className="font-medium text-foreground">{data.invoice_code ?? "-"}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3">
                                    <p className="text-muted-foreground-1">Kode Pembayaran</p>
                                    <p className="font-medium text-foreground">{data.payment_code ?? "-"}</p>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3 md:col-span-2">
                                    <p className="text-muted-foreground-1">Informasi Penerima</p>
                                    <div className="mt-1 flex items-center gap-3">
                                        {data.momo_recipient_avatar ? (
                                            <img
                                                src={data.momo_recipient_avatar}
                                                alt="Avatar penerima"
                                                className="h-10 w-10 rounded-full object-cover"
                                            />
                                        ) : null}
                                        <div className="space-y-0.5">
                                            <p className="font-medium text-foreground">
                                                {data.momo_nick_name ?? "-"}
                                            </p>
                                            <p className="text-xs text-muted-foreground-1">
                                                ID Penerima: {data.momo_recipient_code ?? "-"}
                                            </p>
                                            <p className="text-xs text-muted-foreground-1">
                                                Telepon: {data.momo_recipient_phone ?? "-"}
                                            </p>
                                            <p className="text-xs text-muted-foreground-1">
                                                Kode Agen: {data.agent_code ?? "-"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-line-2 bg-layer p-3 md:col-span-2">
                                    <p className="text-muted-foreground-1">URL Checkout</p>
                                    {data.checkout_url ? (
                                        <a
                                            href={data.checkout_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-medium text-primary hover:underline break-all"
                                        >
                                            {data.checkout_url}
                                        </a>
                                    ) : (
                                        <p className="font-medium text-foreground">-</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </>
    );
}
